import { useState } from 'react'
import { AdminApiError, login } from '@/api/client'
import { useRefreshAfterAuthChange } from '@/api/queries'

function messageFor(err: unknown): string {
  if (err instanceof AdminApiError) {
    // DELIBERATELY IDENTICAL for a wrong email and a wrong password. The server
    // knows which it was and logs it; saying so here would confirm to an
    // attacker which admin addresses exist and hand over half the credential.
    if (err.status === 401) return 'Invalid email or password.'
    if (err.status === 429) return 'Too many attempts. Wait a moment and try again.'
    return `Request failed (${err.status}).`
  }
  if (err instanceof Error) return err.message
  return 'Something went wrong.'
}

/**
 * Shown whenever the portal can't establish who's asking (r0-authn): no session,
 * an expired one, or a 401 from any /admin/v2 call.
 *
 * Email + password. This replaced a WebAuthn-only lane, which is a genuine
 * security downgrade — a passkey is phishing-resistant and bound to hardware,
 * and a password is neither. It was done because the passkey lane had become
 * unusable AND undiagnosable: a credential registered from a phone had its
 * cross-device assertion rejected every time, and the server answered a flat
 * "Invalid email, code, or passkey" while discarding the real reason, so there
 * was nothing to debug from. Re-adding passkeys later is desirable; the
 * credentials and webauthn_ceremonies tables were kept for exactly that.
 *
 * The server refuses to start without ADMIND_USERNAME/ADMIND_PASSWORD, so there
 * is no "unconfigured" state in which this form silently lets anyone through.
 */
export function LoginView() {
  const refresh = useRefreshAfterAuthChange()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await login(email.trim(), password)
      // Do NOT keep the password in state after this point — the session lives
      // in a Secure HttpOnly cookie the server set, and nothing here needs it.
      setPassword('')
      refresh()
    } catch (err) {
      setError(messageFor(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4">
      <div className="w-full max-w-[380px] rounded-[10px] border border-border bg-surface p-6 shadow-sm">
        <div className="mb-5 text-[15px] font-bold tracking-tight">
          Sunsword <span className="font-medium text-ink-2">Admin</span>
        </div>

        <form onSubmit={handleLogin} className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-ink-2">
            Email
            <input
              type="email"
              required
              autoFocus
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-[7px] border border-border bg-canvas px-2.5 py-1.5 text-[13px] text-ink outline-none focus:border-accent"
              placeholder="you@example.com"
            />
          </label>

          <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-ink-2">
            Password
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-[7px] border border-border bg-canvas px-2.5 py-1.5 text-[13px] text-ink outline-none focus:border-accent"
            />
          </label>

          {error && <p className="text-[12px] text-critical">{error}</p>}

          <button
            type="submit"
            disabled={busy}
            className="cursor-pointer rounded-[7px] bg-accent px-3 py-1.5 text-[12.5px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Signing in…' : 'Log in'}
          </button>
        </form>
      </div>
    </div>
  )
}
